import env from "@/env.mjs";
import { useGeo, usePathname } from "@/i18n/routing";
import { getBaseUrl } from "@/lib/utils/getBaseUrl";
import { selectionItemToGTAGProduct } from "@/lib/utils/gtm";
import { useSelection, useToken } from "@frend-digital/centra/client";
import { CustomAdapter, EventSubscription } from "@frend-digital/events/client";
import { depictAdapter, getDPC } from "@frend-digital/events/depict";
import {
  type BeforeEvent,
  centraGTMAdapter,
  sendGTMEvent,
} from "@frend-digital/events/gtm";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useGlobalData } from "../GlobalDataContext/GlobalDataContext";
import { getUserData, setUserData, useUserData } from "./user_data";

export const useDPC = () => {
  const { country } = useGeo();
  const token = useToken();
  return useQuery({
    queryKey: ["DPC", token, country],
    queryFn: async () => {
      const dpc = await getDPC(env.NEXT_PUBLIC_DEPICT_MERCHANT, {
        auto_observe: true,
        market: country.toLowerCase(),
      });

      dpc.setSessionId(token);

      return dpc;
    },
    enabled: !!token,
  });
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useBeforeGtmEvent = () => {
  const { data: centra } = useSelection();
  const { country } = useGeo();

  // Add custom data to event parameters
  const beforeGtmEvent: BeforeEvent = useCallback(
    async (e) => {
      // Delay is set to push the event right after Centra gets forceUpdated.
      await delay(10);

      if (e.event === "purchase") {
        await delay(2000);

        const purchaseResult = {
          ...e,
        };

        purchaseResult.ecommerce["value_ex_vat"] =
          purchaseResult.ecommerce.value - (purchaseResult.ecommerce.tax || 0);

        return purchaseResult;
      }

      const userData = getUserData();

      const result = {
        ...e,
        ...userData,
      };

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      result.ecommerce.country = country || "";

      if (e.event === "add_to_cart") {
        const lastItem = e.ecommerce.items.at(-1);
        e.ecommerce.items = lastItem
          ? [
              {
                ...lastItem,
                quantity: 1,
              },
            ]
          : [];
      }

      return result;
    },
    [country],
  );

  return centra ? beforeGtmEvent : undefined;
};

const logAdapter = () =>
  new CustomAdapter((e) => {
    console.log(e.event, e.data);
  });

const PageImpression = () => {
  const pathname = usePathname();
  const geo = useGeo();
  const { userdata } = useGlobalData();
  const url = getBaseUrl();

  const isTriggering = useRef(false);
  const isInitialLoad = useRef(true);

  const getPageType = useCallback(() => {
    if (pathname === "/" || pathname === `/${geo.geo}`) {
      return "home_page";
    }
    if (pathname.includes("/product/")) {
      return "product_detail_page";
    }
    if (pathname.includes("/products/")) {
      return "“product_listing_page”";
    }
    if (pathname.includes("/checkout")) {
      return "checkout";
    }
    if (pathname.includes("login")) {
      return "login";
    }
    if (pathname.includes("account")) {
      return "account";
    }
    if (pathname.includes("summary")) {
      return "order_confirmation";
    }

    return "other";
  }, [geo.geo, pathname]);

  useEffect(() => {
    const userData = getUserData();

    if (isTriggering.current) {
      return;
    }

    isTriggering.current = true;

    const triggerType = isInitialLoad.current ? "hard" : "soft";
    isInitialLoad.current = false;

    sendGTMEvent({
      event: "page_impression",
      page_path: pathname,
      page_info: {
        url:
          typeof window !== "undefined"
            ? window.location.href
            : `${url}${pathname}`,
        virtal_url: `${url}${pathname}`,
        page_path: `${pathname}`,
        page_title: typeof window !== "undefined" ? document.title : "",
        page_type: getPageType(),
        page_referrer: typeof window !== "undefined" ? document.referrer : "",
        market: geo.country,
        locale: geo.geo,
        currency: userdata?.currency || "NOK",
        trigger_type: triggerType,
      },
      ...userData,
      ecommerce: null,
    });

    isTriggering.current = false;
  }, [geo.country, geo.geo, getPageType, pathname, url, userdata?.currency]);

  return null;
};

const Events = () => {
  const data = useGlobalData();
  const { data: selection } = useSelection();
  const userData = useUserData();

  const beforeGtmEvent = useBeforeGtmEvent();

  const initialCentraAdapter = useMemo(() => {
    return centraGTMAdapter({
      beforeEvent: beforeGtmEvent,
      selection,
      formatter: (product) => {
        return selectionItemToGTAGProduct(
          product,
          data.userdata?.currency || "NOK",
        );
      },
    });
  }, [beforeGtmEvent, data.userdata?.currency, selection]);

  const adapters = useMemo(
    () => [
      new CustomAdapter(async (e) => {
        if (e.event === "purchase") return;

        initialCentraAdapter.consume(e);
      }),
      depictAdapter(env.NEXT_PUBLIC_DEPICT_MERCHANT, {
        auto_observe: true,
        market: data.userdata?.country?.toLowerCase() || "no",
      }),
      logAdapter(),
    ],
    [
      beforeGtmEvent,
      data.userdata?.currency,
      data.userdata?.country,
      selection,
    ],
  );

  useEffect(() => {
    setUserData(userData).catch(console.error);
  }, [userData]);

  return (
    <>
      <PageImpression />
      <EventSubscription adapters={adapters} />
    </>
  );
};

export default Events;
